/**
 *      ▄ █
 *    █ █ █▄▄  LOADBALANCER CLIENT
 *    █ █ ▄ █
 *    ▀     ▀  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../../core/components';
import { CheckoutService } from '../../../services/component';
import { Component, Input } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import { FormGroup } from '@angular/forms';
import { LoadingService, LogService, NotifyService, StripeService } from '../../../../../../core/services';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-add-payment-method',
    templateUrl: './add-payment-method.component.html',
    styleUrls: ['./add-payment-method.component.scss']
})
export class AddPaymentMethodComponent extends BaseComponent {
    /**
     * Step index set by parent
     */
    @Input() stepIndex: number;

    /**
     * Set if adding a new card only.
     */
    @Input() public newCard: boolean = false;

    /**
     * Store the active step.
     */
    public activeStep: number = 1;

    /**
     * User form.
     */
    public formGroup: FormGroup;

    /**
     * Complete submission messages.
     */
    public paymentCompleteDetails = {
        paymentMethodTypes: '',
        status: ''
    };

    constructor(
        private checkoutService: CheckoutService,
        private loadingService: LoadingService,
        private logService: LogService,
        private notifyService: NotifyService,
        private router: Router,
        private stripeService: StripeService,
    ) {
        super();

        this.subscribeToCheckoutService();
    }

    /**
     * Subscribe to check out service.
     * @private
     */
    private subscribeToCheckoutService(): void {
        this.checkoutService.activeStepEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (stepIndex: any): void => {
                    this.activeStep = stepIndex;
                    if (this.activeStep === this.stepIndex) {
                        this.createPaymentElement();
                    }
                }
            });

        this.checkoutService.childSubmitEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (stepIndex: any): void => {
                    this.activeStep = stepIndex;
                    if (this.activeStep === this.stepIndex) {
                        this.submitPaymentMethod();
                    }
                }
            });
    }

    /**
     * Create payment element.
     * @private
     */
    private createPaymentElement(): void {
        const paymentElement: any = this.stripeService.createPaymentElement();
        paymentElement.on('change', (event: { complete: any; value: { type: any; }; }): void => {
            if (this.activeStep === this.stepIndex) {
                this.checkoutService.isStepValidEmitter.next(event.complete);
            }
        });
        paymentElement.mount('#payment-element');
    }

    /**
     * Submit payment method
     * @private
     */
    private submitPaymentMethod(): void {
        this.notifyService.removeNotificationById('card-error');
        this.stripeService.submitPaymentMethod()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    if (data.payment_method_types && data.status) {
                        this.paymentCompleteDetails.paymentMethodTypes = data.payment_method_types;
                        this.paymentCompleteDetails.status = data.status;
                    }
                    if (this.newCard) {
                        this.goBackToBillingOverview();
                    } else {
                        this.checkoutService.nextStep();
                    }

                    this.loadingService.loadingButtonEmitter.next({ id: 'add-new-card', state: false });
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'PaymentMethodComponent:submitPaymentMethod',
                        'Error submitting payment method:' + error.message
                    );
                    this.notifyService.error('Error: ' + error.message, { id: 'card-error' });
                    this.loadingService.loadingButtonEmitter.next({ id: 'add-new-card', state: false });
                    this.checkoutService.isStepValidEmitter.next(false);
                }
            });
    }

    /**
     * Help function to take the user back to the page they came from.
     */
    public goBackToBillingOverview(): void {
        // TODO remove setTimeout when websockets can update the status.
        const timerId = setTimeout((): void => {
            this.router.navigateByUrl(
                '/cm/organisation/billing/overview',
                { state: { tabFilter: 'methods' } }
            ).then().catch();
            clearInterval(timerId);
        }, 1500);
    }
}
